import React, { useState, useMemo, useEffect } from "react";
import { Box } from "@mui/material";
import RepaymentForm from "./components/RepaymentForm";
import RepaymentSchedule from "./components/RepaymentSchedule";
import { generateHPSchedule } from "./utils/calculations";
import { validateSchedule } from "./utils/validation";
import { groupByYA } from "./utils/dateUtils";
import { processYARounding } from "./utils/rounding";
import RepaymentLoading from "./components/RepaymentLoading";
import FinalView from "./components/FinalView";

const HirePurchaseTool = ({ onAddToHPSchedule, caTypeOptions, caSettings }) => {
  const [step1Complete, setStep1Complete] = useState(false);
  const [step1Schedule, setStep1Schedule] = useState(null);
  const [step1Validation, setStep1Validation] = useState(null);
  const [step1Inputs, setStep1Inputs] = useState(null);
  const [step2Complete, setStep2Complete] = useState(false);
  const [step2Results, setStep2Results] = useState(null);
  const [isRepaymentLoading, setIsRepaymentLoading] = useState(false);
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [error, setError] = useState(null);

  const handleRepaymentFormSubmit = (inputs) => {
    try {
      // Clear any previous errors
      setError(null);

      // Handle optional deposit
      const deposit = inputs.deposit || 0;

      // Generate HP schedule
      const schedule = generateHPSchedule({
        assetCost: inputs.assetCost,
        deposit: deposit,
        tenor: inputs.tenor,
        instalmentAmount: inputs.instalmentAmount,
        firstInstalmentDate: inputs.firstInstalmentDate,
        inAdvance: inputs.inAdvance,
      });

      // Validate schedule
      const pv = inputs.assetCost - deposit;
      const pmt = inputs.instalmentAmount;
      const n = inputs.tenor;
      const validation = validateSchedule(schedule, pv, pmt, n);

      setStep1Schedule(schedule);
      setStep1Validation(validation);
      setStep1Inputs(inputs);
      setStep1Complete(true);
      setStep2Complete(false);
      setStep2Results(null);
    } catch (err) {
      setError(
        err.message ||
          "An error occurred while generating the schedule. Please check your inputs and try again."
      );
      console.error("Error generating schedule:", err);
    }
  };

  const handleStep2Submit = (inputs) => {
    // Start loading
    setIsRepaymentLoading(true);
    setLoadingStartTime(Date.now());

    // Store Step 2 inputs
    setStep2Results({
      inputs: inputs,
    });

    // Set complete immediately (loading will be handled by useEffect)
    setStep2Complete(true);
  };

  const handleCancel = () => {
    setStep2Complete(false);
    setStep2Results(null);
    setIsRepaymentLoading(false);
    setLoadingStartTime(null);
  };

  // Handle loading duration - ensure at least 2 seconds
  useEffect(() => {
    if (isRepaymentLoading && loadingStartTime && step2Complete) {
      const elapsed = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, 1000 - elapsed);

      if (remainingTime > 0) {
        const timer = setTimeout(() => {
          setIsRepaymentLoading(false);
          setLoadingStartTime(null);
        }, remainingTime);
        return () => clearTimeout(timer);
      } else {
        setIsRepaymentLoading(false);
        setLoadingStartTime(null);
      }
    }
  }, [isRepaymentLoading, loadingStartTime, step2Complete]);

  // Compute Step 2 results when inputs are available
  const step2ComputedResults = useMemo(() => {
    if (!step2Results || !step1Schedule || !step1Inputs) {
      return null;
    }

    const { inputs } = step2Results;

    // Group by YA - pass inAdvance flag from Step 1 inputs
    const yaGroups = groupByYA(
      step1Schedule,
      inputs.fyEndMonth,
      step1Inputs.inAdvance
    );

    // Apply rounding and adjustments
    const { roundedGroups, adjustment } = processYARounding(
      yaGroups,
      step1Inputs.assetCost
    );

    return {
      roundedGroups,
      adjustment,
      originalTotal: adjustment.originalTotal,
      adjustedTotal: adjustment.adjustedTotal,
    };
  }, [step2Results, step1Schedule, step1Inputs]);

  const handleBackToForm = () => {
    setStep1Complete(false);
  };

  const handleReset = () => {
    setStep1Complete(false);
    setStep1Schedule(null);
    setStep1Validation(null);
    setStep1Inputs(null);
    setStep2Complete(false);
    setStep2Results(null);
    setIsRepaymentLoading(false);
    setLoadingStartTime(null);
    setError(null);
  };

  const handleClearSavedForm = () => {
    setStep1Schedule(null);
    setStep1Validation(null);
    setStep1Inputs(null);
    setStep2Complete(false);
    setStep2Results(null);
    setIsRepaymentLoading(false);
    setLoadingStartTime(null);
    setError(null);
  };

  return (
    <Box>
      {!step1Complete ? (
        <RepaymentForm
          onSubmit={handleRepaymentFormSubmit}
          calculationError={error}
          setCalculationError={setError}
          savedStep1Inputs={step1Inputs}
          onClearSaved={handleClearSavedForm}
        />
      ) : isRepaymentLoading ? (
        <RepaymentLoading />
      ) : !step2Complete ? (
        <RepaymentSchedule
          caTypeOptions={caTypeOptions}
          caSettings={caSettings}
          schedule={step1Schedule}
          validation={step1Validation}
          step1Inputs={step1Inputs}
          step2Results={step2Results}
          onBackToForm={handleBackToForm}
          onStep2Submit={handleStep2Submit}
        />
      ) : (
        <FinalView
          schedule={step1Schedule}
          step1Inputs={step1Inputs}
          step2Results={step2Results}
          step2ComputedResults={step2ComputedResults}
          onCancel={handleCancel}
          onAddToHPSchedule={onAddToHPSchedule}
          onReset={handleReset}
        />
      )}
    </Box>
  );
};

export default HirePurchaseTool;
